import * as THREE from 'three';
import { GLTFLoader } from 'three/addons/loaders/GLTFLoader.js';
import { DRACOLoader } from 'three/addons/loaders/DRACOLoader.js';
import { EXRLoader } from 'three/addons/loaders/EXRLoader.js';

class Preloader {
    constructor() {
        // Configuration
        this.loadingManager = new THREE.LoadingManager();
        this.resources = [];
        this.totalResources = 0;
        this.loadedResources = 0;
        this.alreadyHidden = false;
        
        // Créer le conteneur du préloader
        this.preloaderContainer = document.createElement('div');
        this.preloaderContainer.id = 'preloader';
        document.body.appendChild(this.preloaderContainer);
        
        // Initialize the MP4 video animation
        this.initVideoPreloader();
        
        // Configure the loading manager
        this.setupLoadingManager();
    }
    
    initVideoPreloader() {
        // Create the video element
        this.video = document.createElement('video');
        this.video.src = '/static/js/preloader/Tree_for_loader.mp4';
        this.video.muted = true;
        this.video.loop = true;
        this.video.playsInline = true; // Important pour iOS
        this.video.autoplay = true;
        this.video.controls = false;
        this.video.style.width = '100%';
        this.video.style.height = '100%';
        this.video.style.objectFit = 'contain';
        
        // Add the video to the container
        this.preloaderContainer.appendChild(this.video);
        
        // Add the loading text
        this.progressText = document.createElement('div');
        this.progressText.className = 'progress-text';
        this.progressText.textContent = '...loading...';
        this.progressText.style.color = 'blue';
        this.progressText.style.fontFamily = "'Iceberg', cursive";
        this.preloaderContainer.appendChild(this.progressText);
        
        // Start the video
        this.video.play().catch(e => {
            console.warn('Autoplay a été bloqué :', e);
            // Fallback en cas de blocage de l'autoplay
            const playButton = document.createElement('button');
            playButton.className = 'play-button';
            playButton.onclick = () => {
                this.video.play();
                playButton.remove();
            };
            this.preloaderContainer.appendChild(playButton);
        });
    }
    
    setupLoadingManager() {
        this.loadingManager.onProgress = (url, loaded, total) => {
            this.loadedResources = loaded;
            this.totalResources = total;
            const progress = (loaded / total) * 100;
            
            // Update the progress text
            this.progressText.textContent = `Chargement ${Math.round(progress)}%`;
        };
        
        this.loadingManager.onLoad = () => {
            console.log('Chargement terminé');
            this.hidePreloader();
        };

        this.loadingManager.onError = (url) => {
            console.error('Erreur de chargement:', url);
            this.hidePreloader();  // ← Ne pas rester bloqué en cas d'erreur
        };

        this.loadSafetyTimeout = setTimeout(() => {
            console.warn('Preloader timeout - affichage forcé');
            this.hidePreloader();
        }, 30000);
        
    }
    
    addResource(url) {
        // Add a resource to load
        this.resources.push(url);
    }
    
    startLoading() {
        // If no resource has been added, simulate a small delay
        if (this.resources.length === 0) {
            setTimeout(() => {
                this.hidePreloader();
            }, 2000);
            return;
        }

        this.loadAssets = {};
        
        // Load all resources
        this.resources.forEach(url => {
            // Déterminer le type de ressource en fonction de l'extension
            if (url.endsWith('.jpg') || url.endsWith('.png') || url.endsWith('.webp')) {
                new THREE.TextureLoader(this.loadingManager).load(url, (texture) => {
                    this.loadAssets[url] = texture;
                });
            } else if (url.endsWith('.exr')) {
                new EXRLoader(this.loadingManager).load(url, (texture) => {
                    this.loadAssets.exrTexture = texture;
                });
            } else if (url.endsWith('.glb')) {
                const dracoLoader = new DRACOLoader();
                dracoLoader.setDecoderPath('https://www.gstatic.com/draco/v1/decoders/');
                const gltfLoader = new GLTFLoader(this.loadingManager);
                gltfLoader.setDRACOLoader(dracoLoader);
                gltfLoader.load(url, (gltf) => {
                    this.loadAssets.gltf = gltf;
                });
            } else {
                // For other types of resources
                fetch(url).then(response => {
                    if (!response.ok) throw new Error(`HTTP error! status: ${response.status}`);
                });
            }
        });
    }
    
    hidePreloader() {
        if (this.alreadyHidden) return;
        this.alreadyHidden = true;

        if (this.loadSafetyTimeout) {
            clearTimeout(this.loadSafetyTimeout);
            this.loadSafetyTimeout = null;
        }

        // Exit animation
        this.preloaderContainer.classList.add('fade-out');
        
        // Immediately hide the progress text
        if (this.progressText) {
            this.progressText.style.display = 'none';
        }
        
        setTimeout(() => {
            // Stop the video and clean up
            if (this.video) {
                this.video.pause();
                this.video.remove();
            }
            
            // Completely remove the container
            if (this.preloaderContainer && this.preloaderContainer.parentNode) {
                this.preloaderContainer.parentNode.removeChild(this.preloaderContainer);
            }
            
            // If a callback is defined, execute it
            if (typeof this.onLoadComplete === 'function') {
                this.onLoadComplete();
            }
        }, 1000);
    }
}

// Export the Preloader class
export { Preloader };